import React from 'react';
import BaseTemplate from './base.js';
import Details from './details.js';
import {ImageElement,RatingElement,ContentElement} from './elements.js';
import * as u from '../utils.js'
import DEFS from '../defs.js'

/*---------------------------------------------*/

const MOBILE_STYLE = {
	container: {
		paddingTop: 32,
		paddingBottom: 32,
		paddingLeft: 24,
		paddingRight: 24
	},
	bubbleInner: {
		paddingTop: 28,
		paddingBottom: 16,
		paddingLeft: 16,
		paddingRight: 16
	},
	content: {
		lineHeight: 1.5
	}
}

const BREAKPOINTS_STYLES = {
	400: {
		container: {
			paddingTop: 32,
			paddingBottom: 32,
			paddingLeft: 24,
			paddingRight: 24
		},
		bubbleInner: {
			paddingTop: 20,
			paddingBottom: 20,
			paddingLeft: 16,
			paddingRight: 16
		},
		content: {
			lineHeight: 1.5
		}
	},
	720: {
		container: {
			paddingTop: 32,
			paddingBottom: 32,
			paddingLeft: 40,
			paddingRight: 40
		},
		bubbleInner: {
			paddingTop: 20,
			paddingBottom: 20,
			paddingLeft: 24,
			paddingRight: 24
		},
		content: {
			lineHeight: 1.5
		}
	},
	960: {
		container: {
			paddingTop: 32,
			paddingBottom: 32,
			paddingLeft: 48,
			paddingRight: 48
		},
		bubbleInner: {
			paddingTop: 20,
			paddingBottom: 20,
			paddingLeft: 32,
			paddingRight: 32
		},
		content: {
			lineHeight: 1.75
		}
	},
	Infinity: {
		container: {
			paddingTop: 32,
			paddingBottom: 32,
			paddingLeft: 56,
			paddingRight: 56
		},
		bubbleInner: {
			paddingTop: 20,
			paddingBottom: 20,
			paddingLeft: 40,
			paddingRight: 40
		},
		content: {
			lineHeight: 1.75
		}
	}
}

/*---------------------------------------------*/

export default class DefaultTemplate extends BaseTemplate {
	
	reduceRatingPositions(composition) {
		composition = composition.join('-');

		if (composition === 'image-rating-content-details')
			composition = 'image-content-rating-details';
		
		else if (composition === 'image-rating-details-content')
			composition = 'image-details-rating-content';
				
		else if (composition === 'details-rating-content-image')
			composition = 'details-content-rating-image';
			
		else if (composition === 'details-rating-image-content')
			composition = 'details-image-rating-content';
			
		else if (composition === 'content-image-rating-details')
			composition = 'content-rating-image-details';
			
		else if (composition === 'content-details-rating-image')
			composition = 'content-rating-details-image';
	
		return composition.split('-');
	}

	getComposition(keepRating) {
		let textPosition = this.context.get('template.textposition');
		let composition = (textPosition === 'top') ? ['content','details'] : ['details','content'];
		
		let imageEnabled = this.context.get('fields.image.enabled');
		if (imageEnabled) {
			let imagePosition = this.context.get('template.imageposition');
			switch (imagePosition) {
				case 'top':
					composition.splice(0,0,'image');
					break;
				
				case 'middle':
					composition.splice(1,0,'image');
					break;

				case 'bottom':
					composition.push('image');
					break;

				default:
					console.log("Invalid image position",imagePosition);
					break;		
			}
		}
		
		let ratingEnabled = this.context.get('fields.rating.enabled');
		if (ratingEnabled && (keepRating || this.props.data.rating)) {
			let ratingPosition = this.context.get('template.ratingposition');
			ratingPosition = imageEnabled ? 
				['above','between','between2','below'].indexOf(ratingPosition) :
				['above','between','below'].indexOf(ratingPosition);

			composition.splice(ratingPosition,0,'rating');

			if (imageEnabled)
				composition = this.reduceRatingPositions(composition);
		}
		
		return composition.join('-');
	}
	
	render() {
		let data = this.props.data;

		const SIZES_PRESETS = {xs:40, s:64, m:96, l:120, xl:160};
		let imageSize = this.context.get('style.image.size',64,56);
		imageSize = imageSize in SIZES_PRESETS ? SIZES_PRESETS[imageSize] : 1*imageSize;

		let alignment = this.context.get('template.default.alignment','center');
		let responsiveStyle = this.context.isMobile() ? MOBILE_STYLE : u.getBreakpointStyle(BREAKPOINTS_STYLES,this.props.width);

		let aligns = {
			top: {},
			size: {}
		}

		let styles = {
			container: {
				...this.props.baseStyle,
				...responsiveStyle.container,
				textAlign: alignment,
				width: '100%'
			},
			content: {
				...responsiveStyle.content
			},
			image: {},
			rating: {},
			details: {}
		}

		if (this.context.get('style.lineHeight')) {
			styles.content.lineHeight = this.context.get('style.lineHeight');
		}

		if (this.props.content) {
			return (
				<div className="testimonial" style={styles.container} align-group={this.props.alignGroup+DEFS.align.testimonial} ref={this.container}>
					{this.props.content}
				</div>
			)
		}

		let composition = this.getComposition();
		switch (composition) {
			/*----- 2 elements -----*/
			case 'content-details':
				styles.content.marginBottom = 19;
				break;
		
			case 'details-content':
				styles.details.marginBottom = 22;
				break;
					
			// NO IMAGE
			case 'rating-details-content':
				styles.rating.marginBottom = 12;
				styles.details.marginBottom = 22;
				break;

			case 'details-rating-content':
				styles.details.marginBottom = 17;
				styles.rating.marginBottom = 5;
				break;
					
			case 'rating-content-details':
				styles.rating.marginBottom = 13;
				styles.content.marginBottom = 20;
				break;
			
			case 'content-rating-details':
				styles.content.marginBottom = 8;
				styles.rating.marginBottom = 20;
				break;
			
			case 'content-details-rating':
				styles.content.marginBottom = 20;
				styles.details.marginBottom = 9;
				break;
			
			case 'details-content-rating':
				styles.details.marginBottom = 22;
				styles.rating.marginTop = 7;
				break;

			/*----- 4 elements -----*/
			// x = ['image-content-details','image-details-content','details-content-image','details-image-content','content-image-details','content-details-image',]
			// copy([].concat.apply([], x.map(function(c) { return [0,1,2,3].map(function(index){ d = c.split('-'); d.splice(index,0,'rating'); return d.join('-')}) })).join('\n'))

			/* composition #1 */
			case 'image-content-details':
			case 'rating-image-content-details':
				styles.rating.marginBottom = 16;
				styles.image.marginBottom = 21;
				styles.content.marginBottom = 19;
				break;
			case 'image-rating-content-details':
				styles.image.marginBottom = 24;
				styles.rating.marginBottom = 4;
				styles.content.marginBottom = 20;
				break;
			case 'image-content-rating-details':
				styles.image.marginBottom = 20;
				styles.content.marginBottom = 8;
				styles.rating.marginBottom = 20;
				aligns.top.rating = false;
				break;
			case 'image-content-details-rating':
				styles.image.marginBottom = 20;
				styles.content.marginBottom = 20;
				styles.details.marginBottom = 9;
				aligns.top.rating = false;
				break;

			/* composition #2 */
			case 'image-details-content':
			case 'rating-image-details-content':
				styles.rating.marginBottom = 16;
				styles.image.marginBottom = 12;
				styles.content.marginTop = 22;
				aligns.top.content = false;
				break;
			case 'image-rating-details-content':
				styles.image.marginBottom = 8;
				styles.rating.marginBottom = 20;
				styles.details.marginBottom = 22;
				break;
			case 'image-details-rating-content':
				styles.image.marginBottom = 12;
				styles.details.marginBottom = 17;
				styles.rating.marginBottom = 5;
				aligns.top.image = aligns.top.details = aligns.top.rating = aligns.top.content = false;
				break;
			case 'image-details-content-rating':
				styles.image.marginBottom = 12;
				styles.details.marginBottom = 22;
				styles.content.marginBottom = 7;
				aligns.top.rating = aligns.top.content = false;
				break;

			/* composition #3 */
			case 'details-content-image':
			case 'rating-details-content-image':
				styles.rating.marginBottom = 12;
				styles.details.marginBottom = 22;
				styles.content.marginBottom = 23;
				aligns.top.image = aligns.top.rating = aligns.top.content = false;
				break;
			case 'details-rating-content-image':
				styles.details.marginBottom = 17;
				styles.rating.marginBottom = 5;
				styles.content.marginBottom = 23;
				break;
			case 'details-content-rating-image':
				styles.details.marginBottom = 22;
				styles.rating.marginTop = 7;
				styles.image.marginTop = 24;
				aligns.top.image = aligns.top.details = aligns.top.rating = aligns.top.content = false;
				break;
			case 'details-content-image-rating':
				styles.details.marginBottom = 21;
				styles.content.marginBottom = 23;
				styles.image.marginBottom = 16;
				aligns.top.image = aligns.top.details = aligns.top.rating = aligns.top.content = false;
				break;

			/* composition #6 */
			case 'details-image-content':
			case 'rating-details-image-content':
				styles.rating.marginBottom = 12;
				styles.details.marginBottom = 9;
				styles.image.marginBottom = 21;
				aligns.top.rating = aligns.top.content = false;
				break;
			case 'details-rating-image-content':
				styles.details.marginBottom = 17;
				styles.rating.marginBottom = 8;
				styles.image.marginBottom = 21;
				break;
			case 'details-image-rating-content':
				styles.details.marginBottom = 9;
				styles.image.marginBottom = 24;
				styles.rating.marginBottom = 5;
				aligns.top.details = aligns.top.rating = aligns.top.content = false;
				break;
			case 'details-image-content-rating':
				styles.details.marginBottom = 9;
				styles.image.marginBottom = 21;
				styles.content.marginBottom = 7;
				aligns.top.details = aligns.top.rating = aligns.top.content = false;
				break;

			/* composition #4 */
			case 'content-image-details':
			case 'rating-content-image-details':
				styles.rating.marginBottom = 13;
				styles.content.marginBottom = 31;
				styles.image.marginBottom = 12;
				break;
			case 'content-rating-image-details':
				styles.content.marginBottom = 7;
				styles.rating.marginBottom = 32;
				styles.image.marginBottom = 12;
				aligns.top.rating = false;
				break;
			case 'content-image-rating-details':
				styles.image.marginTop = 31;
				styles.rating.marginTop = 8;
				styles.details.marginTop = 20;
				break;
			case 'content-image-details-rating':
				styles.content.marginBottom = 31;
				styles.image.marginBottom = 12;
				styles.rating.marginTop = 17;
				break;

			/* composition #5 */
			case 'content-details-image':
			case 'rating-content-details-image':
				styles.rating.marginBottom = 13;
				styles.content.marginBottom = 27;
				styles.image.marginTop = 9;
				break;
			case 'content-rating-details-image':
				styles.content.marginBottom = 8;
				styles.rating.marginBottom = 20;
				styles.details.marginBottom = 9;
				aligns.top.rating = false;
				break;
			case 'content-details-rating-image':
				styles.content.marginBottom = 27;
				styles.details.marginBottom = 17;
				styles.rating.marginBottom = 8;
				break;
			case 'content-details-image-rating':
				styles.content.marginBottom = 27;
				styles.details.marginBottom = 9;
				styles.image.marginBottom = 8;
				break;

			default:
				console.error("Invalid composition value",composition);
				break;
		}

		
		let partialDisplay = true;
		switch (this.props.part) {
			case 'top':
				composition = composition.split(/-?image-?/)[0];
				break;
			
			case 'bottom':
				composition = composition.split(/-?image-?/)[1];
				break;
			
			case 'thumbnail':
				let scaleBy = (this.context.get('layout.thumbnails.scaling',15) / 100);
				let boxShadow = null;
				let border = null;
				if (this.props.selected) {
					imageSize = imageSize*(1 + scaleBy);

					let shadowX = this.context.get('layout.thumbnails.selected.boxShadow.x');
					let shadowY = this.context.get('layout.thumbnails.selected.boxShadow.y');
					let shadowBlur = this.context.get('layout.thumbnails.selected.boxShadow.blur');
					let shadowSpread = this.context.get('layout.thumbnails.selected.boxShadow.spread');
					let shadowColor = u.renderColor(this.context.get('layout.thumbnails.selected.boxShadow.color','black'));
					boxShadow = `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowSpread}px ${shadowColor}`

					let borderWidth = this.context.get('layout.thumbnails.selected.border.width');
					let borderColor = u.renderColor(this.context.get('layout.thumbnails.selected.border.color','black'));
					border = `${borderWidth}px solid ${borderColor}`;
				}

				styles.image = {
					...styles.image,
					marginLeft: !this.props.first && this.context.get('layout.thumbnails.spacing1',20)/2,
					marginRight: !this.props.last && this.context.get('layout.thumbnails.spacing1',20)/2,
					marginTop: (styles.image.marginTop || 0) + this.context.get('layout.thumbnails.vspacing1',0),
					marginBottom: (styles.image.marginBottom || 0) + this.context.get('layout.thumbnails.vspacing2',0),
					boxShadow: boxShadow,
					border: border
				}

				composition = 'image';
				break;
			
			default:
				partialDisplay = false;
				break;
		}

		this.getComposition(true).split('-').forEach((type,index,list) => {
			if (aligns.top[type] === false || index === 0)
				return;

			aligns.top[type] = (list.length-index);
		});

		let alignProps = {};
		if (this.props.alignGroup) {
			Object.keys(aligns.top).forEach(key => {
				if (aligns.top[key]) {
					alignProps[key] = {
						[key === 'details'? 'align-top' : 'alignTop']: this.props.alignGroup+aligns.top[key]
					}
				}
			})

			Object.keys(aligns.size).forEach(key => {
				if (aligns.size[key]) {
					alignProps[key] = {
						[key === 'details'? 'align-group' : 'alignGroup']: this.props.alignGroup+aligns.size[key]
					}
				}
			})
		}

		let components = {
			image: <ImageElement {...alignProps.image} image={data.author_image} size={imageSize} baseStyle={styles.image} key="image" />,
			// content: <ContentElement text={data.text} alignGroup={this.props.alignGroup && (this.props.alignGroup+DEFS.align.text)} baseStyle={styles.content} key="content" />,
			content: <ContentElement {...alignProps.content} text={data.text} baseStyle={styles.content} key="content" />,
			rating: <RatingElement {...alignProps.rating} rating={data.rating} baseStyle={styles.rating} key="rating" />,
			details: <div {...alignProps.details} style={styles.details} key="details"><Details data={data} alignment={alignment} /></div>
		}
		
		var content = composition.split('-').map((type) => components[type]);
		if (partialDisplay)
			return content;

		let hasContainer = this.context.get('template.container',true);
		if (!hasContainer) {
			styles.container.paddingTop = 0;
			styles.container.paddingBottom = 0;
			styles.container.paddingLeft = 0;
			styles.container.paddingRight = 0;
		}
		
		return (
			<div className="testimonial" style={styles.container} align-group={this.props.alignGroup && (this.props.alignGroup+DEFS.align.testimonial)} ref={this.container}>
				{content}
			</div>
		)
	}
}

/*---------------------------------------------*/
